import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Table, Button, Row, Col, Card, Modal, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faMinus, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import CartContext from '../context/CartContext';
import getImageUrl from '../utils/getImageUrl';

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, updateQuantity, removeFromCart, totalPrice, loading } = useContext(CartContext);
  const [showConfirm, setShowConfirm] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  const handleRemoveClick = (item) => {
    setItemToRemove(item);
    setShowConfirm(true);
  };

  const confirmRemove = () => {
    if (itemToRemove && itemToRemove._id !== undefined) {
      removeFromCart(itemToRemove._id);
    }
    setShowConfirm(false);
    setItemToRemove(null);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  // Mobile-friendly quantity controls component
  const QuantityControls = ({ item }) => (
    <div className="d-flex align-items-center justify-content-between mt-2">
      <div className="d-flex align-items-center">
        <Button 
          variant="outline-secondary" 
          size="sm"
          onClick={() => updateQuantity(item._id, item.quantity - 1)}
          className="quantity-btn"
        >
          <FontAwesomeIcon icon={faMinus} />
        </Button>
        <span className="mx-2 fs-5">{item.quantity}</span>
        <Button 
          variant="outline-secondary" 
          size="sm"
          onClick={() => updateQuantity(item._id, item.quantity + 1)}
          className="quantity-btn"
        >
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      </div>
      <span className="ms-3 fs-5 fw-bold">₦{(item.price * item.quantity).toLocaleString()}</span>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="text-center my-5">
        <h4>Loading your cart...</h4>
      </div>
    );
  }

  // After loading is done, check cart items
  if (!cartItems || cartItems.length === 0) {
    return (
      <div className="text-center py-5">
        <h3>Your cart is empty</h3>
        <Button variant="danger" onClick={() => handleNavigation('/menu')} className="mt-3 px-4">
          Browse Menu
        </Button>
      </div>
    );
  }

  return (
    <Container className="my-3 my-md-5">
      <h1 className="mb-3 mb-md-4">
        <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
        Your Cart
      </h1>
      <div className="d-none d-md-block">
        <Table responsive className="align-middle">
          <thead>
            <tr>
              <th>Item</th>
              <th>Price</th>
              <th>Quantity</th>
              <th>Subtotal</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cartItems.map(item => (
              <tr key={item._id}>
                <td>
                  <div className="d-flex align-items-center">
                    <img 
                      src={getImageUrl(item.image)} 
                      alt={item.name} 
                      width="60" 
                      height="60" 
                      className="rounded me-3"
                    />
                    <div>
                      <span>{item.name}</span>
                      {item.isCustomOrder && (
                        <div className="text-muted small">
                          Custom Order
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>₦{item.price.toLocaleString()}</td>
                <td>
                  {item.isCustomOrder ? (
                    <span className="mx-3">{item.quantity}</span>
                  ) : (
                    <div className="d-flex align-items-center">
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={() => updateQuantity(item._id, item.quantity - 1)}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </Button>
                      <span className="mx-3">{item.quantity}</span>
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        onClick={() => updateQuantity(item._id, item.quantity + 1)}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </Button>
                    </div>
                  )}
                </td>
                <td>₦{(item.price * item.quantity).toLocaleString()}</td>
                <td>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => handleRemoveClick(item)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Mobile Cards (shown only on mobile) */}
      <div className="d-md-none">
        <ListGroup variant="flush">
          {cartItems.map(item => (
            <ListGroup.Item key={item._id} className="py-3 px-0 border-bottom">
              <Row className="g-0 align-items-center">
                <Col xs={3}>
                  <img 
                    src={getImageUrl(item.image)} 
                    alt={item.name} 
                    className="rounded img-fluid"
                    style={{ maxHeight: '80px', objectFit: 'cover' }}
                  />
                </Col>
                <Col xs={7} className="ps-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">{item.name}</h6>
                      <p className="mb-0 text-muted">₦{item.price.toLocaleString()}</p>
                    </div>
                    <Button 
                      variant="link" 
                      size="sm"
                      className="text-danger p-0"
                      onClick={() => {
                        setItemToRemove(item);
                        setShowConfirm(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                  <QuantityControls item={item} />
                </Col>
              </Row>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </div>

      {/* Order Summary - Visible on all screens */}
      <Card className="border-0 shadow-sm mt-4">
        <Card.Body>
          <div className="mb-2">
            <div className="d-flex justify-content-between">
              <span>Subtotal:</span>
              <span>₦{totalPrice.subtotal.toLocaleString()}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span>Delivery Fee:</span>
              <span>{totalPrice.deliveryFee === 0 ? "Free" : `₦${totalPrice.deliveryFee.toLocaleString()}`}</span>
            </div>
          </div>
          <hr />
          <h5 className="d-flex justify-content-between mb-3">
            <span>Total ({cartItems.reduce((sum, item) => sum + item.quantity, 0)} items):</span>
            <span>₦{totalPrice.totalPrice.toLocaleString()}</span>
          </h5>
          <Button 
            variant="danger" 
            size="lg" 
            className="w-100 py-2"
            onClick={() => navigate('/checkout')}
          >
            Proceed to Checkout
          </Button>
        </Card.Body>
      </Card>

      {/* Removal Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Removal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {itemToRemove && itemToRemove.price !== undefined && itemToRemove.quantity !== undefined ? (
            <>
              <p>Remove <strong>{itemToRemove.name}</strong> from your cart?</p>
              <div className="d-flex align-items-center">
                <img 
                  src={getImageUrl(itemToRemove.image)} 
                  alt={itemToRemove.name} 
                  width="60" 
                  height="60" 
                  className="rounded me-3"
                />
                <div>
                  <p className="mb-1">₦{itemToRemove.price.toLocaleString()} × {itemToRemove.quantity}</p>
                  <p className="mb-0 text-muted">Subtotal: ₦{(itemToRemove.price * itemToRemove.quantity).toLocaleString()}</p>
                </div>
              </div>
            </>
          ) : (
            <p>Loading item details...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemove}>
            Remove Item
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CartPage;		